import { Injectable } from '@angular/core';
import { CheckinWaitingSearchCondition } from '@app/checkin-waiting-search/sub-components/checkin-waiting-search-condition-form/checkin-waiting-search-condition-form.component';
import { CheckinBaseClass } from '../../components';
import { GetFlightsResultStoreService } from '../get-flights-result-store/get-flights-result-store.service';
import { PostFlightsSearchFlightsRequest } from 'src/sdk_checkin';
import { ApiErrorResponseService, ErrorsHandlerService, PageLoadingService, SystemDateService } from '@lib/services';
import { CheckinCommonStoreService } from '../checkin-common-store/checkin-common-store.service';
import { Router } from '@angular/router';
import { PREVIOUS_PAGE_IDENTIFIER } from '@conf/previous-page-identifier.config';
import { CHECKIN_ROUTES } from '@conf/routes.config';
import { observeOn, withLatestFrom } from 'rxjs/operators';
import { Airport, ErrorType, PageType } from '@lib/interfaces';
import { asyncScheduler } from 'rxjs';
import { ErrorCodeConstants } from '@conf/app.constants';
import { DEPARTURE_TIME_POSSIBLE_VALUES } from '@conf/waiting-search';

@Injectable()
export class CheckinWaitingSearchService extends CheckinBaseClass {
  /**
   * チェックイン空港空席待ち人数検索APIエラー一覧
   * key=エラーコード
   * value=エラーメッセージID
   */
  private readonly API_ERROR_CODE_TO_MESSAGE_KEY: { [errorCode: string]: string } = {
    // 検索条件に合致するフライトが存在しない
    [ErrorCodeConstants.ERROR_CODES.EBAZ000731]: 'E1254',
  };

  /**
   * 検索API成功時、検索条件をチェックイン共通に格納するための一時保存用の変数
   */
  private savedParam?: {
    departureAirport: Airport;
    arrivalAirport: Airport;
    operationDate: string;
    shapedTimeRange: string[];
  };

  constructor(
    private apiErrorResponseService: ApiErrorResponseService,
    private checkinCommonStoreService: CheckinCommonStoreService,
    private errorsHandlerService: ErrorsHandlerService,
    private getFlightsResultStoreService: GetFlightsResultStoreService,
    private pageLoadingService: PageLoadingService,
    private router: Router,
    private systemDateService: SystemDateService
  ) {
    super();

    // 検索APIの成功と失敗を監視
    this.watchGetFlightsFailure();
    this.watchGetFlightsSuccess();
  }
  override destroy(): void {}

  /**
   * 出発時刻選択の入力値から時刻に変換する
   * @param timeRange 出発時刻選択の入力値
   * @returns hh:mm:ssの形式に整形した値の配列
   */
  calcParamFromRangeSlider(timeRange: number[]): string[] {
    return timeRange.map((time) => {
      const hour = Math.floor(time / 60)
        .toString()
        .padStart(2, '0');
      const minute = (time % 60).toString().padStart(2, '0');
      return `${hour}:${minute}:00`;
    });
  }

  /** 出発時刻選択の時刻から入力値に変換する */
  calcRangeSliderFromParam(times: string) {
    // 入力された時間を表す文字列を区切り文字「:」で分割
    const splittedTime = times.split(':');

    // 時間と秒を数値に変換
    const hour = Number(splittedTime[0]);
    const minute = Number(splittedTime[1]);

    // 時間と分からレンジスライダーに設定するための数値型に変換
    // NOTE:秒はレンジスライダーの入力に関わらないので除外
    return hour * 60 + minute;
  }

  /**
   * 画面入力フォームの入力値からパラメータを生成し、API実行
   * @param condition 画面入力フォームの入力値
   */
  checkinWaitingSearch(condition: CheckinWaitingSearchCondition) {
    const shapedTimeRange = this.calcParamFromRangeSlider(condition.timeRange);
    const param: PostFlightsSearchFlightsRequest = {
      departureLocationCode: condition.departureAirport.airport_code,
      arrivalLocationCode: condition.arrivalAirport.airport_code,
      operationDate: this.systemDateService.getFormattedSystemDate('yyyy-MM-dd'),
      departureTimeFrom: shapedTimeRange[0],
      departureTimeTo: shapedTimeRange[1],
    };
    this.savedParam = {
      departureAirport: condition.departureAirport,
      arrivalAirport: condition.arrivalAirport,
      operationDate: param.operationDate,
      shapedTimeRange,
    };
    this.getFlightsResultStoreService.callApi(param);
  }

  /**
   * エラーコードを受け取り、エラーメッセージIDを返却する
   * @param apiErrorCode
   * @returns 指定されたエラーコードに紐づくエラーメッセージID、存在しない場合はundefined
   */
  public getErrorMessageKey(apiErrorCode: string): string | undefined {
    return this.API_ERROR_CODE_TO_MESSAGE_KEY[apiErrorCode];
  }

  /**
   * 空港空席待ち人数検索APIの結果が失敗だった場合の処理
   */
  public watchGetFlightsFailure() {
    const result$ = this.getFlightsResultStoreService
      .getIsFailure$()
      .pipe(withLatestFrom(this.apiErrorResponseService.getApiErrorResponse$(), (_, state) => state));
    this.subscribeService(result$, (state) => {
      const errorLength = state?.errors?.length;
      if (!!errorLength && errorLength > 0) {
        const code = state?.errors?.[0]?.code;
        const matchErrorMsgId = code && this.getErrorMessageKey(code);
        if (matchErrorMsgId) {
          this.errorsHandlerService.setRetryableError(PageType.PAGE, {
            errorMsgId: matchErrorMsgId,
            apiErrorCode: code,
          });
        } else {
          this.errorsHandlerService.setNotRetryableError({ errorType: ErrorType.SYSTEM });
        }
      }
      this.pageLoadingService.endLoading();
    });
  }

  /**
   * 空港空席待ち人数検索APIの結果が成功だった場合の処理
   */
  public watchGetFlightsSuccess() {
    // セッションストレージと同期するFlightsResultStoreの監視中に画面遷移を行い、画面遷移先でStoreの読み取り処理があった場合にセッションストレージからの復元で再度subscribeが発生してしまい無限ループになってしまう
    // 画面遷移処理を非同期呼び出しにし、無限ループを回避するため、asyncSchedulerを利用する
    const result$ = this.getFlightsResultStoreService.getResultStateIfSuccessful$().pipe(observeOn(asyncScheduler));
    this.subscribeService(result$, (_) => {
      this.pageLoadingService.endLoading();
      this.checkinCommonStoreService.updateCheckinCommon({
        departureLocationCode: this.savedParam?.departureAirport.airport_code,
        departureAirportName: this.savedParam?.departureAirport.name,
        arrivalLocationCode: this.savedParam?.arrivalAirport.airport_code,
        arrivalAirportName: this.savedParam?.arrivalAirport.name,
        departureDate: this.savedParam?.operationDate,
        departureTimeFrom: this.savedParam?.shapedTimeRange[0],
        departureTimeTo: this.savedParam?.shapedTimeRange[1],
      });
      this.navigateToCheckinWaitingNumber();
    });
  }

  /**
   * 空港空席待ち人数検索結果画面への遷移
   */
  private navigateToCheckinWaitingNumber() {
    this.checkinCommonStoreService.updateCheckinCommon({
      previousPageIdentifier: PREVIOUS_PAGE_IDENTIFIER.CHECKIN_WAITING_SEARCH,
    });
    this.router.navigate([CHECKIN_ROUTES.waitingNumber]);
  }

  /**
   * 空港空席待ち人数検索画面の検索条件について復元を行うため以下の条件でパラメータの取得を行う
   * 1.previousPageIdentifier≠checkinWaitingNumberの場合は復元なしで画面としての初期値を設定
   * 2.上記以外の場合以下それぞれを設定
   * 　　- departureTimeFrom： チェックイン共通.departureTimeFromの値が存在すればその値を設定、存在しなければ画面としての初期値を設定
   *  - departureTimeTo： チェックイン共通.departureTimeToの値が存在すればその値を設定、存在しなければ画面としての初期値を設定
   *  - departureLocationCode: チェックイン共通.departureLocationCodeの値が存在すればその値を設定、存在しなければ画面としての初期値を設定
   *  - arrivalLocationCode: チェックイン共通.arrivalLocationCodeの値が存在すればその値を設定、存在しなければ画面としての初期値を設定
   * @returns 空港空席待ち人数検索画面の検索条件の初期値として設定する値
   */
  getWaitingSearchInitialParam() {
    const checkinCommon = this.checkinCommonStoreService.checkinCommon;
    if (checkinCommon.previousPageIdentifier !== PREVIOUS_PAGE_IDENTIFIER.CHECKIN_WAITING_NUMBER) {
      return {
        departureTimeFrom: DEPARTURE_TIME_POSSIBLE_VALUES.min,
        departureTimeTo: DEPARTURE_TIME_POSSIBLE_VALUES.max,
        departureLocationCode: '',
        arrivalLocationCode: '',
      };
    }

    return {
      departureTimeFrom: checkinCommon.departureTimeFrom
        ? this.calcRangeSliderFromParam(checkinCommon.departureTimeFrom)
        : DEPARTURE_TIME_POSSIBLE_VALUES.min,
      departureTimeTo: checkinCommon.departureTimeTo
        ? this.calcRangeSliderFromParam(checkinCommon.departureTimeTo)
        : DEPARTURE_TIME_POSSIBLE_VALUES.max,
      departureLocationCode: checkinCommon.departureLocationCode ? checkinCommon.departureLocationCode : '',
      arrivalLocationCode: checkinCommon.arrivalLocationCode ? checkinCommon.arrivalLocationCode : '',
    };
  }
}
